import { queryKey } from 'Shared/const/queryKey';
import { createReturnUrl } from 'Shared/utils/client';
import { LINK } from '@constants';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@viewup/ui';
import type { GetViewupAccountsRes } from 'app/_entities/sns-account';
import { AccountStatus } from 'app/myviewup/account/_features/ui/AccountStatusRow';
import { useToggleActiveSnsAccount } from 'app/viewing/_entities/common/api/useToggleActiveSnsAccount';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import type { PlatformCategoryCategoryEnum } from 'viewup-api';

type AccountProps = {
  account: GetViewupAccountsRes[number];
  children?: ReactNode;
};
export const Account = (props: AccountProps) => {
  const { account, children } = props;
  const router = useRouter();
  const queryClient = useQueryClient();

  const { mutate: toggleActive } = useToggleActiveSnsAccount({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKey.viewingCommon.accounts.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKey.click.campaigns().queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKey.posting.availableList.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queryKey.posting.overviewList._def,
        });
      },
    },
  });

  return (
    <AccountStatus
      account={{
        hasError: !!account.accountError,
        snsType: account.viewingPlatform as PlatformCategoryCategoryEnum,
        userName: account.accountName,
      }}
      styledComponents={{
        root: {
          gap: 0,
          onClick: () => {
            if (account.accountError) return;
            toggleActive({ snsAccountUuid: account.accountUuid });
          },
          ...(!account.accountError && !account.isActive && { css: { cursor: 'pointer' } }),
          ...(account.isActive && {
            border: '1 solid border.success',
            shadow: 'center.normal',
          }),
        },
      }}
    >
      {account.isActive && (
        <AccountStatus.RightContent>
          <Badge colorVariant='green' content='적립중' variant='rectangle' />
        </AccountStatus.RightContent>
      )}
      <AccountStatus.BottomContent gap={0} mt={0}>
        {account.accountError && (
          <AccountStatus.ErrorBox
            mt={8}
            onClick={() =>
              router.push(
                `${LINK.MY_VIEWUP_ACCOUNT}${createReturnUrl(LINK.VIEWING_CLICK)}&snsId=${account.accountName}&platform=${account.accountPlatform}`,
              )
            }
            text='재등록이 필요해요'
          />
        )}
        {children}
      </AccountStatus.BottomContent>
    </AccountStatus>
  );
};
